"use client";

import { useState, useEffect, useMemo } from "react";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, BarChart2, Activity, TrendingUp, ExternalLink } from "lucide-react";
import { BsPin, BsPinFill } from "react-icons/bs";
import { TbChartLine } from "react-icons/tb";
import Link from "next/link";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { marketPageUrl } from "@/lib/source-url";
import { usePinnedBoxes, PinnedBox } from "@/contexts/PinnedBoxContext";
import { useT } from "@/hooks/useT";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
} from "recharts";

const TradingModal = dynamic(
  () => import("@/app/components/TradingModal"),
  { ssr: false }
);

interface Market {
  id: string;
  slug: string;
  eventSlug?: string | null;
  title: string;
  image: string | null;
  yesPrice: number;
  noPrice: number;
  volume24hr: number;
  view: "orderbook" | "trades" | "chart";
  clobTokenIds?: string[];
}

interface MarketTileProps {
  market: Market;
  onRemove: (id: string) => void;
  onToggleView: (id: string, view?: "orderbook" | "trades" | "chart") => void;
}

export default function MarketTile({ market, onRemove, onToggleView }: MarketTileProps) {
  const t = useT();
  const router = useRouter();
  const [tradingModal, setTradingModal] = useState<{ isOpen: boolean; outcome: "YES" | "NO" } | null>(null);
  const { addPinnedBox, removePinnedBox, isPinned } = usePinnedBoxes();

  // Click YES/NO → if we have an eventSlug, route to the event page with
  // the market and outcome pre-selected so the sidebar Place Trade picks
  // up the right sub-market + side. Same flow as PolymarketMarketCard
  // and the event grid rows. Falls back to opening the trading modal
  // when there's no parent event (rare for binary markets).
  const handleOutcomeClick = (outcome: "YES" | "NO") => {
    if (market.eventSlug) {
      router.push(
        `/polymarket/event/${market.eventSlug}?market=${market.slug}&outcome=${outcome.toLowerCase()}`
      );
      return;
    }
    setTradingModal({ isOpen: true, outcome });
  };
  const pinned = isPinned(market.id);

  const handleTogglePin = () => {
    if (pinned) {
      removePinnedBox(market.id);
    } else {
      const pinnedBox: PinnedBox = {
        id: market.id,
        slug: market.slug,
        eventSlug: market.eventSlug ?? null,
        title: market.title,
        image: market.image,
        yesPrice: market.yesPrice,
        noPrice: market.noPrice,
        volume24hr: market.volume24hr,
        view: market.view,
        type: "market",
      };
      addPinnedBox(pinnedBox);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: market.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden flex flex-col h-[400px] transition-colors hover:border-gray-300 dark:hover:border-white/[0.1]"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-[#1A1A1A] flex items-center justify-between gap-2">
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-[5px] cursor-grab active:cursor-grabbing transition-colors"
          aria-label={t("degen.tile.dragReorder")}
          title={t("degen.tile.dragReorder")}
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </button>

        <div className="flex-1 flex items-center gap-2 min-w-0">
          {market.image && (
            <img
              src={market.image}
              alt={market.title}
              className="w-6 h-6 rounded-[5px] object-cover flex-shrink-0"
            />
          )}
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate" title={market.title}>
            {market.title}
          </h3>
        </div>

        <div className="flex items-center gap-0.5">
          <button
            onClick={handleTogglePin}
            className={`p-1.5 rounded-[5px] transition-colors ${
              pinned
                ? "bg-gray-900/10 dark:bg-white/10 text-gray-900 dark:text-white"
                : "hover:bg-gray-100 dark:hover:bg-white/[0.06] text-gray-400"
            }`}
            aria-label={pinned ? t("degen.tile.unpin") : t("degen.tile.pin")}
            title={pinned ? t("degen.tile.unpin") : t("degen.tile.pin")}
          >
            {pinned ? (
              <BsPinFill className="w-3.5 h-3.5" />
            ) : (
              <BsPin className="w-3.5 h-3.5" />
            )}
          </button>

          <Link
            href={marketPageUrl({
              marketSlug: market.slug,
              eventSlug: market.eventSlug,
              source: 'polymarket',
            })}
            prefetch={false}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-[5px] transition-colors"
            aria-label={t("degen.tile.viewMarket")}
            title={t("degen.tile.viewMarket")}
          >
            <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
          </Link>

          <button
            onClick={() => onRemove(market.id)}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-[5px] transition-colors"
            aria-label={t("degen.tile.removeMarket")}
            title={t("degen.tile.removeMarket")}
          >
            <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
          </button>
        </div>
      </div>

      {/* Prices — same visual language as /event MarketsGrid (soft-tinted
          green/red pills, bold inline label + price). Keeps DegenBox
          consistent with the event page so users see one outcome style. */}
      <div className="p-4 border-b border-gray-200 dark:border-[#1A1A1A]">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleOutcomeClick("YES")}
            className="py-3 rounded-[5px] font-inter-tight font-bold text-sm tabular-nums transition-colors bg-[#359a5e]/15 text-[#3db468] hover:bg-[#359a5e]/25"
          >
            {t("bub.label.yes")} {(Number(market.yesPrice) || 0.5) * 100 < 0.05 ? '0.0' : ((Number(market.yesPrice) || 0.5) * 100).toFixed(1)}¢
          </button>
          <button
            onClick={() => handleOutcomeClick("NO")}
            className="py-3 rounded-[5px] font-inter-tight font-bold text-sm tabular-nums transition-colors bg-[#cb3131]/15 text-[#cb3131] hover:bg-[#cb3131]/25"
          >
            {t("bub.label.no")} {(Number(market.noPrice) || 0.5) * 100 < 0.05 ? '0.0' : ((Number(market.noPrice) || 0.5) * 100).toFixed(1)}¢
          </button>
        </div>
      </div>

      {/* Trading Modal */}
      {tradingModal?.isOpen && (
        <TradingModal
          isOpen={tradingModal.isOpen}
          onClose={() => setTradingModal(null)}
          marketSlug={market.slug}
          outcome={tradingModal.outcome}
          outcomePrice={tradingModal.outcome === "YES" ? market.yesPrice : market.noPrice}
          marketQuestion={market.title}
          marketImage={market.image}
        />
      )}

      {/* View Tabs */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-[#1A1A1A] flex items-center gap-1">
        <button
          onClick={() => onToggleView(market.id, "orderbook")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
            market.view === "orderbook"
              ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]"
          }`}
        >
          <BarChart2 className="w-3.5 h-3.5" />
          {t("degen.tile.tab.book")}
        </button>
        <button
          onClick={() => onToggleView(market.id, "trades")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
            market.view === "trades"
              ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]"
          }`}
        >
          <Activity className="w-3.5 h-3.5" />
          {t("degen.tile.tab.trades")}
        </button>
        <button
          onClick={() => onToggleView(market.id, "chart")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
            market.view === "chart"
              ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]"
          }`}
        >
          <TbChartLine className="w-3.5 h-3.5" />
          {t("degen.tile.tab.chart")}
        </button>
      </div>

      {/* Content Area - Order Book, Trades, or Chart */}
      <div className="flex-1 overflow-hidden">
        {market.view === "orderbook" ? (
          <OrderBookView market={market} />
        ) : market.view === "trades" ? (
          <TradesView market={market} />
        ) : (
          <MiniChartView market={market} />
        )}
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-white/[0.02]">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-gray-500">
            <TrendingUp className="w-3 h-3 text-gray-400" />
            <span>{t("degen.tile.vol24h")}</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
            ${market.volume24hr >= 1000000
              ? `${(market.volume24hr / 1000000).toFixed(1)}M`
              : `${(market.volume24hr / 1000).toFixed(1)}K`}
          </span>
        </div>
      </div>
    </div>
  );
}

function OrderBookView({ market }: { market: Market }) {
  const t = useT();
  const basePrice = Number(market.yesPrice) || 0.5;
  const spread = 0.01;
  const increment = 0.01;

  const bids = [
    { price: Math.max(0.01, basePrice - spread), size: 150 },
    { price: Math.max(0.01, basePrice - spread - increment), size: 300 },
    { price: Math.max(0.01, basePrice - spread - increment * 2), size: 450 },
  ];

  const asks = [
    { price: Math.min(0.99, basePrice + spread), size: 200 },
    { price: Math.min(0.99, basePrice + spread + increment), size: 350 },
    { price: Math.min(0.99, basePrice + spread + increment * 2), size: 500 },
  ];

  const calculatedSpread = asks[0].price - bids[0].price;

  return (
    <div className="h-full p-3 overflow-auto">
      <div className="space-y-3">
        {/* Asks */}
        <div>
          <div className="text-[10px] font-bold text-red-400 mb-2 uppercase tracking-wide">
            {t("degen.tile.asks")}
          </div>
          <div className="space-y-1">
            {asks.map((ask, i) => (
              <div
                key={i}
                className="flex justify-between text-xs hover:bg-red-50 dark:hover:bg-red-900/10 px-2 py-1 rounded-[5px] cursor-pointer transition-colors"
              >
                <span className="text-red-400 font-semibold tabular-nums">
                  {(ask.price * 100).toFixed(1)}¢
                </span>
                <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                  {ask.size}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Spread */}
        <div className="text-center py-2 bg-gray-100 dark:bg-white/[0.04] rounded-[5px]">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {t("degen.tile.spread")}: {(calculatedSpread * 100).toFixed(1)}¢
          </span>
        </div>

        {/* Bids */}
        <div>
          <div className="text-[10px] font-bold text-emerald-500 mb-2 uppercase tracking-wide">
            {t("degen.tile.bids")}
          </div>
          <div className="space-y-1">
            {bids.map((bid, i) => (
              <div
                key={i}
                className="flex justify-between text-xs hover:bg-emerald-50 dark:hover:bg-emerald-900/10 px-2 py-1 rounded-[5px] cursor-pointer transition-colors"
              >
                <span className="text-emerald-500 font-semibold tabular-nums">
                  {(bid.price * 100).toFixed(1)}¢
                </span>
                <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                  {bid.size}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TradesView({ market }: { market: Market }) {
  const t = useT();
  const basePrice = Number(market.yesPrice) || 0.5;

  const trades = [
    { price: basePrice, size: 100, side: "buy", time: t("degen.tile.timeAgo.minutes", { n: 2 }) },
    { price: Math.max(0.01, basePrice - 0.01), size: 50, side: "sell", time: t("degen.tile.timeAgo.minutes", { n: 5 }) },
    { price: Math.min(0.99, basePrice + 0.02), size: 200, side: "buy", time: t("degen.tile.timeAgo.minutes", { n: 8 }) },
    { price: Math.max(0.01, basePrice - 0.03), size: 75, side: "sell", time: t("degen.tile.timeAgo.minutes", { n: 12 }) },
    { price: Math.min(0.99, basePrice + 0.01), size: 150, side: "buy", time: t("degen.tile.timeAgo.minutes", { n: 15 }) },
  ];

  return (
    <div className="h-full p-3 overflow-auto">
      <div className="text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
        {t("degen.tile.recentTrades")}
      </div>
      <div className="space-y-1">
        {trades.map((trade, i) => (
          <div
            key={i}
            className="grid grid-cols-3 gap-2 text-xs py-1.5 px-2 hover:bg-gray-50 dark:hover:bg-white/[0.03] rounded-[5px] transition-colors"
          >
            <span
              className={`font-semibold tabular-nums ${
                trade.side === "buy"
                  ? "text-emerald-500"
                  : "text-red-400"
              }`}
            >
              {(trade.price * 100).toFixed(1)}¢
            </span>
            <span className="text-gray-500 dark:text-gray-400 text-right tabular-nums">
              {trade.size}
            </span>
            <span className="text-gray-400 text-right">
              {trade.time}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniChartView({ market }: { market: Market }) {
  const t = useT();
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // Tolerate market.clobTokenIds saved as a JSON-encoded string by the
  // older MarketSearchModal — gamma returns this field as a string for
  // some endpoints and existing DegenBox slots persist that shape.
  const initialIds = (() => {
    const raw = market.clobTokenIds as unknown;
    if (Array.isArray(raw)) return raw.filter((x) => typeof x === 'string') as string[];
    if (typeof raw === 'string') {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr.filter((x) => typeof x === 'string') as string[];
      } catch { /* fall through */ }
      if ((raw as string).includes(',')) {
        return (raw as string).split(',').map((s) => s.trim()).filter(Boolean);
      }
    }
    return undefined;
  })();
  const [clobTokenIds, setClobTokenIds] = useState<string[] | undefined>(initialIds);

  useEffect(() => {
    async function fetchClobTokenIds() {
      if (clobTokenIds && clobTokenIds.length >= 2) return;
      if (!market.slug) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/polymarket/market/${market.slug}`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.clobTokenIds) {
            let tokenIds: string[];
            if (typeof data.clobTokenIds === 'string') {
              tokenIds = data.clobTokenIds.startsWith('[')
                ? JSON.parse(data.clobTokenIds)
                : data.clobTokenIds.split(',');
            } else {
              tokenIds = data.clobTokenIds;
            }
            if (tokenIds && tokenIds.length >= 2) {
              setClobTokenIds(tokenIds);
              return;
            }
          }
        }
      } catch {
        // Fallback: will show "Chart not available"
      }
      setLoading(false);
    }

    fetchClobTokenIds();
  }, [market.slug, clobTokenIds]);

  useEffect(() => {
    async function fetchPriceHistory() {
      if (!clobTokenIds || clobTokenIds.length < 2) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [yesResponse, noResponse] = await Promise.all([
          fetch(`/api/polymarket/prices-history?tokenId=${clobTokenIds[0]}&interval=1w&fidelity=60`),
          fetch(`/api/polymarket/prices-history?tokenId=${clobTokenIds[1]}&interval=1w&fidelity=60`),
        ]);

        const [yesResult, noResult] = await Promise.all([
          yesResponse.json(),
          noResponse.json(),
        ]);

        const yesHistory = yesResult.success ? yesResult.data.history : [];
        const noHistory = noResult.success ? noResult.data.history : [];

        const priceMap = new Map<number, { yesPrice: number | null; noPrice: number | null }>();

        yesHistory.forEach((point: { time: number; price: number }) => {
          priceMap.set(point.time, { yesPrice: point.price * 100, noPrice: null });
        });

        noHistory.forEach((point: { time: number; price: number }) => {
          const existing = priceMap.get(point.time);
          if (existing) {
            existing.noPrice = point.price * 100;
          } else {
            priceMap.set(point.time, { yesPrice: null, noPrice: point.price * 100 });
          }
        });

        const dataPoints = Array.from(priceMap.entries())
          .map(([timestamp, prices]) => ({
            timestamp: timestamp * 1000,
            yesPrice: prices.yesPrice,
            noPrice: prices.noPrice,
          }))
          .sort((a, b) => a.timestamp - b.timestamp);

        setChartData(dataPoints);
      } catch (err) {
        setChartData([]);
      } finally {
        setLoading(false);
      }
    }

    fetchPriceHistory();
  }, [clobTokenIds]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!clobTokenIds || clobTokenIds.length < 2) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <p className="text-xs text-gray-400 text-center">
          {t("degen.tile.chartUnavailable")}
        </p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <p className="text-xs text-gray-400 text-center">
          {t("degen.tile.noTradingData")}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full p-2">
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`yesGradient-${market.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id={`noGradient-${market.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f87171" stopOpacity={0.2} />
              <stop offset="100%" stopColor="#f87171" stopOpacity={0} />
            </linearGradient>
          </defs>

          <XAxis dataKey="timestamp" hide />
          <YAxis domain={[0, 100]} hide />

          <ReferenceLine y={50} stroke="#4B5563" strokeDasharray="3 3" strokeOpacity={0.3} />

          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const yesPrice = payload.find(p => p.dataKey === 'yesPrice')?.value as number;
                const noPrice = payload.find(p => p.dataKey === 'noPrice')?.value as number;
                return (
                  <div className="bg-gray-900 dark:bg-[#18181D] px-2.5 py-1.5 rounded-[5px] shadow-xl border border-gray-700 dark:border-white/[0.1] text-xs">
                    {yesPrice != null && (
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-400 font-semibold">YES</span>
                        <span className="text-white tabular-nums">{yesPrice.toFixed(0)}%</span>
                      </div>
                    )}
                    {noPrice != null && (
                      <div className="flex items-center gap-2">
                        <span className="text-red-400 font-semibold">NO</span>
                        <span className="text-white tabular-nums">{noPrice.toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            }}
          />

          <Area
            type="monotone"
            dataKey="yesPrice"
            stroke="#10b981"
            strokeWidth={2}
            fill={`url(#yesGradient-${market.id})`}
            connectNulls
            dot={false}
          />

          <Area
            type="monotone"
            dataKey="noPrice"
            stroke="#f87171"
            strokeWidth={2}
            fill={`url(#noGradient-${market.id})`}
            connectNulls
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
